import { el, svg } from "../core/dom";
import type { ResolvedBrand } from "./colors";
import type { Fields, FieldErrors } from "./validation";

type IdleHandlers = {
  onInput: (field: keyof Fields, value: string) => void;
  onBlur: (field: keyof Fields) => void;
  onSubmit: () => void;
};

export type IdleRefs = {
  root: HTMLElement;
  fieldInputs: Record<keyof Fields, HTMLInputElement>;
  fieldErrors: Record<keyof Fields, HTMLElement>;
  submitBtn: HTMLButtonElement;
  generalError: HTMLElement;
};

function header(brand: ResolvedBrand): HTMLElement {
  const logoImg = el("img", {
    src: brand.logo,
    alt: `${brand.name} logo`,
    className: "dpdpa-logo",
    onerror: (ev: Event) => {
      const img = ev.currentTarget as HTMLImageElement;
      img.style.display = "none";
      const wordmark = img.nextElementSibling as HTMLElement | null;
      if (wordmark) wordmark.style.display = "inline-block";
    },
  }) as HTMLImageElement;

  const wordmark = el(
    "span",
    { className: "dpdpa-wordmark", style: { display: "none" } },
    brand.name
  );

  return el("div", { className: "dpdpa-header" }, logoImg, wordmark);
}

function inputRow(
  brand: ResolvedBrand,
  opts: {
    field: keyof Fields;
    label: string;
    type: string;
    placeholder: string;
    value: string;
    error: string | undefined;
    autocomplete?: string;
    onInput: (value: string) => void;
    onBlur: () => void;
  }
): { row: HTMLElement; input: HTMLInputElement; errorSlot: HTMLElement } {
  const inputEl = el("input", {
    type: opts.type,
    className: "dpdpa-input",
    placeholder: opts.placeholder,
    value: opts.value,
    autocomplete: opts.autocomplete,
    "aria-label": opts.label,
    onInput: (ev: Event) => opts.onInput((ev.currentTarget as HTMLInputElement).value),
    onBlur: () => opts.onBlur(),
  }) as HTMLInputElement;

  const errorSlot = el(
    "div",
    {
      className: "dpdpa-field-error",
      role: "alert",
      style: { display: opts.error ? "block" : "none" },
    },
    opts.error || ""
  );

  const labelEl = el("label", { className: "dpdpa-label" }, opts.label);

  const row = el(
    "div",
    { className: "dpdpa-field" },
    labelEl,
    inputEl,
    errorSlot
  );

  return { row, input: inputEl, errorSlot };
}

function footer(brand: ResolvedBrand): HTMLElement {
  return el(
    "div",
    { className: "dpdpa-footer" },
    "Powered by ",
    el("strong", null, brand.name)
  );
}

export function renderIdle(
  brand: ResolvedBrand,
  fields: Fields,
  errors: FieldErrors,
  generalErrorText: string | null,
  handlers: IdleHandlers
): IdleRefs {
  const nameRow = inputRow(brand, {
    field: "company_name",
    label: "Company name",
    type: "text",
    placeholder: "Acme Inc",
    value: fields.company_name,
    error: errors.company_name,
    autocomplete: "organization",
    onInput: (v) => handlers.onInput("company_name", v),
    onBlur: () => handlers.onBlur("company_name"),
  });
  const domainRow = inputRow(brand, {
    field: "company_domain",
    label: "Company domain",
    type: "text",
    placeholder: "acme.com",
    value: fields.company_domain,
    error: errors.company_domain,
    autocomplete: "url",
    onInput: (v) => handlers.onInput("company_domain", v),
    onBlur: () => handlers.onBlur("company_domain"),
  });
  const emailRow = inputRow(brand, {
    field: "email",
    label: "Work email",
    type: "email",
    placeholder: "you@acme.com",
    value: fields.email,
    error: errors.email,
    autocomplete: "email",
    onInput: (v) => handlers.onInput("email", v),
    onBlur: () => handlers.onBlur("email"),
  });

  const generalError = el(
    "div",
    {
      className: "dpdpa-general-error",
      role: "alert",
      style: { display: generalErrorText ? "block" : "none" },
    },
    generalErrorText || ""
  );

  const submitBtn = el(
    "button",
    {
      type: "submit",
      className: "dpdpa-btn-primary",
    },
    "Get my free report"
  ) as HTMLButtonElement;

  const form = el(
    "form",
    {
      className: "dpdpa-form",
      novalidate: "true",
      onSubmit: (ev: Event) => {
        ev.preventDefault();
        handlers.onSubmit();
      },
    },
    el(
      "h2",
      { className: "dpdpa-heading" },
      "Get your free DPDPA compliance report"
    ),
    el(
      "p",
      { className: "dpdpa-subheading" },
      "We'll analyse your site and deliver a compliance assessment PDF."
    ),
    generalError,
    nameRow.row,
    domainRow.row,
    emailRow.row,
    submitBtn
  );

  const card = el(
    "div",
    { className: "dpdpa-card" },
    header(brand),
    form,
    footer(brand)
  );

  return {
    root: card,
    fieldInputs: {
      company_name: nameRow.input,
      company_domain: domainRow.input,
      email: emailRow.input,
    },
    fieldErrors: {
      company_name: nameRow.errorSlot,
      company_domain: domainRow.errorSlot,
      email: emailRow.errorSlot,
    },
    submitBtn,
    generalError,
  };
}

export function renderSubmitting(
  brand: ResolvedBrand,
  fields: Fields
): HTMLElement {
  const refs = renderIdle(brand, fields, {}, null, {
    onInput: () => {},
    onBlur: () => {},
    onSubmit: () => {},
  });
  Object.values(refs.fieldInputs).forEach((inp) => {
    inp.disabled = true;
  });
  refs.submitBtn.disabled = true;
  refs.submitBtn.textContent = "";
  refs.submitBtn.appendChild(
    el("span", { className: "dpdpa-spinner dpdpa-spinner-inline" })
  );
  refs.submitBtn.appendChild(document.createTextNode(" Submitting…"));
  return refs.root;
}

function animatedDots(): HTMLElement {
  return el(
    "span",
    { className: "dpdpa-dots", "aria-hidden": "true" },
    el("span"),
    el("span"),
    el("span")
  );
}

function stepCheck(brand: ResolvedBrand): HTMLElement {
  return el(
    "div",
    {
      className: "dpdpa-step-icon dpdpa-step-icon-done",
      style: { backgroundColor: brand.successColor },
    },
    svg(
      "svg",
      { width: 12, height: 12, viewBox: "0 0 24 24", fill: "none" },
      svg("path", {
        d: "M5 12l5 5L20 7",
        stroke: "#ffffff",
        "stroke-width": 3,
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      })
    )
  );
}

function stepPulse(brand: ResolvedBrand): HTMLElement {
  return el("div", {
    className: "dpdpa-step-icon dpdpa-step-icon-active",
    style: { backgroundColor: brand.primary },
  });
}

type PollStatus = "pending" | "researching" | "analysis_complete";

const STEP_ORDER: Record<PollStatus, number> = {
  pending: 0,
  researching: 0,
  analysis_complete: 2,
};

const STEPS: Array<{ label: (company: string) => string }> = [
  { label: (company) => `Researching ${company}` },
  { label: () => "Analysing DPDPA compliance" },
  { label: () => "Generating your report" },
];

export function renderPolling(
  brand: ResolvedBrand,
  opts: {
    companyName: string;
    subStatus: PollStatus;
    elapsedMs: number;
    pollTimeoutMs: number;
  }
): HTMLElement {
  const pct = Math.min(100, Math.round((opts.elapsedMs / opts.pollTimeoutMs) * 100));
  const progressFill = el("div", {
    className: "dpdpa-progress-fill",
    style: { width: `${pct}%` },
  });
  const progressBar = el(
    "div",
    {
      className: "dpdpa-progress",
      role: "progressbar",
      "aria-label": "Report generation progress",
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": String(pct),
    },
    progressFill
  );

  const currentIdx = STEP_ORDER[opts.subStatus];
  const stepsList = el("div", { className: "dpdpa-steps" });
  STEPS.forEach((step, idx) => {
    if (idx > currentIdx) return;
    const isActive = idx === currentIdx;
    const row = el(
      "div",
      {
        className: `dpdpa-step ${isActive ? "dpdpa-step-active" : "dpdpa-step-done"}`,
      },
      isActive ? stepPulse(brand) : stepCheck(brand),
      el(
        "span",
        {
          className: isActive
            ? "dpdpa-step-label dpdpa-shimmer"
            : "dpdpa-step-label",
        },
        step.label(opts.companyName)
      )
    );
    if (isActive) row.appendChild(animatedDots());
    stepsList.appendChild(row);
  });

  const body = el(
    "div",
    { className: "dpdpa-polling" },
    stepsList,
    el(
      "p",
      { className: "dpdpa-polling-sub" },
      "This usually takes 60 to 120 seconds. Keep this tab open."
    ),
    progressBar
  );

  return el("div", { className: "dpdpa-card" }, header(brand), body, footer(brand));
}

function checkIcon(brand: ResolvedBrand): HTMLElement {
  return el(
    "div",
    {
      className: "dpdpa-check-circle",
      style: { backgroundColor: brand.successColor },
    },
    svg(
      "svg",
      {
        width: 28,
        height: 28,
        viewBox: "0 0 24 24",
        fill: "none",
      },
      svg("path", {
        d: "M5 12l5 5L20 7",
        stroke: "#ffffff",
        "stroke-width": 3,
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      })
    )
  );
}

function isSafeUrl(raw: string): boolean {
  const s = (raw || "").trim();
  if (!s) return false;
  // Allow relative paths or absolute http(s) URLs only.
  if (/^https?:\/\//i.test(s)) return true;
  if (/^\/[^/]/.test(s) || s.startsWith("./") || s.startsWith("../"))
    return true;
  return false;
}

export function renderComplete(
  brand: ResolvedBrand,
  opts: {
    companyName: string;
    reportUrl: string;
    onReset: () => void;
  }
): HTMLElement {
  const safeHref = isSafeUrl(opts.reportUrl) ? opts.reportUrl : "#";
  const downloadBtn = el(
    "a",
    {
      href: safeHref,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "dpdpa-btn-primary dpdpa-btn-link",
    },
    "Download PDF"
  );

  const resetLink = el(
    "a",
    {
      href: "#",
      className: "dpdpa-reset-link",
      onClick: (ev: Event) => {
        ev.preventDefault();
        opts.onReset();
      },
    },
    "Request another report"
  );

  const body = el(
    "div",
    { className: "dpdpa-success" },
    checkIcon(brand),
    el(
      "h3",
      { className: "dpdpa-success-title" },
      `Your DPDPA report for ${opts.companyName} is ready`
    ),
    el(
      "p",
      { className: "dpdpa-success-sub" },
      "Click below to download your compliance assessment PDF."
    ),
    downloadBtn,
    resetLink
  );

  return el("div", { className: "dpdpa-card" }, header(brand), body, footer(brand));
}

export function renderError(
  brand: ResolvedBrand,
  opts: {
    message: string;
    retryable: boolean;
    onRetry: () => void;
    onBack: () => void;
  }
): HTMLElement {
  const retryBtn = el(
    "button",
    {
      type: "button",
      className: "dpdpa-btn-primary",
      onClick: () => (opts.retryable ? opts.onRetry() : opts.onBack()),
    },
    opts.retryable ? "Try again" : "Edit details"
  );

  const body = el(
    "div",
    { className: "dpdpa-error-view" },
    el("h3", { className: "dpdpa-error-title" }, "Something went wrong"),
    el("p", { className: "dpdpa-error-message" }, opts.message),
    retryBtn
  );

  return el("div", { className: "dpdpa-card" }, header(brand), body, footer(brand));
}
